/**
 * @fileoverview Sudoku Game Index Screen
 * @description Game setup where users choose difficulty and play mode
 */

import React, { useState, useCallback, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { router, useLocalSearchParams } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { useTheme, useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import { GameModeSelector } from "@/modules/games";
import {
  SudokuDifficulty,
  DIFFICULTY_CONFIGS,
  SudokuUserStats,
  EMPTY_SUDOKU_STATS,
  SudokuWeekendConfig,
  DEFAULT_WEEKEND_CONFIG,
} from "@/modules/sudoku";
import { DifficultyCard } from "@/modules/sudoku/components/DifficultyCard";
import { SudokuLeaderboard } from "@/modules/sudoku/components/SudokuLeaderboard";

type SessionMode = "free" | "weekend" | "practice";

/**
 * SudokuIndexScreen - Game setup and difficulty selection
 */
export default function SudokuIndexScreen() {
  const { isDark } = useTheme();
  const colors = useThemeColors();
  const { activeProfile, activeWeekend, userTeam } = useAuth();
  const { mode: urlMode } = useLocalSearchParams<{ mode?: string }>();

  const targetUserId = activeProfile?.id;

  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingData, setIsFetchingData] = useState(true);
  const [selectedMode, setSelectedMode] = useState<SessionMode>(
    urlMode === "weekend" ? "weekend" : "free"
  );
  const [gameEnabledForWeekend, setGameEnabledForWeekend] = useState(false);
  const [weekendConfig, setWeekendConfig] = useState<SudokuWeekendConfig>(
    DEFAULT_WEEKEND_CONFIG
  );
  const [puzzleCounts, setPuzzleCounts] = useState<
    Record<SudokuDifficulty, number>
  >({
    easy: 0,
    medium: 0,
    hard: 0,
  });
  const [stats, setStats] = useState<SudokuUserStats>(EMPTY_SUDOKU_STATS);
  const [weekendStats, setWeekendStats] =
    useState<SudokuUserStats>(EMPTY_SUDOKU_STATS);

  // Determine if weekend mode is available
  const isWeekendModeAvailable =
    !!activeWeekend && !!userTeam && gameEnabledForWeekend;
  const weekendUnavailableReason = !activeWeekend
    ? "Geen actief weekend"
    : !userTeam
    ? "Je zit nog niet in een team"
    : !gameEnabledForWeekend
    ? "Niet geactiveerd voor dit weekend"
    : "";

  /**
   * Fetch weekend game config
   */
  const fetchWeekendGameConfig = useCallback(async () => {
    if (!activeWeekend) {
      setGameEnabledForWeekend(false);
      return;
    }

    try {
      const { data } = await supabase
        .from("weekend_game_config")
        .select("is_enabled, points_config, custom_config")
        .eq("weekend_id", activeWeekend.id)
        .eq("game_slug", "sudoku")
        .single();

      setGameEnabledForWeekend(data?.is_enabled ?? false);

      if (data?.custom_config) {
        setWeekendConfig({
          ...DEFAULT_WEEKEND_CONFIG,
          ...(data.custom_config as Partial<SudokuWeekendConfig>),
        });
      }

      if (data?.points_config) {
        const pointsConfig = data.points_config as Record<string, number>;
        setWeekendConfig((prev) => ({
          ...prev,
          points_per_difficulty: {
            easy: pointsConfig.easy ?? prev.points_per_difficulty.easy,
            medium: pointsConfig.medium ?? prev.points_per_difficulty.medium,
            hard: pointsConfig.hard ?? prev.points_per_difficulty.hard,
          },
          perfect_bonus:
            pointsConfig.perfect_bonus ?? prev.perfect_bonus,
        }));
      }
    } catch {
      setGameEnabledForWeekend(false);
    }
  }, [activeWeekend]);

  /**
   * Fetch puzzle counts per difficulty
   */
  const fetchPuzzleCounts = useCallback(async () => {
    try {
      const difficulties: SudokuDifficulty[] = ["easy", "medium", "hard"];
      const counts: Record<SudokuDifficulty, number> = {
        easy: 0,
        medium: 0,
        hard: 0,
      };

      await Promise.all(
        difficulties.map(async (difficulty) => {
          const { count, error } = await supabase
            .from("sudoku_puzzles")
            .select("*", { count: "exact", head: true })
            .eq("difficulty", difficulty)
            .eq("is_active", true);

          if (!error && count !== null) {
            counts[difficulty] = count;
          }
        })
      );

      setPuzzleCounts(counts);
    } catch (err) {
      console.error("[Sudoku] Error fetching puzzle counts:", err);
    }
  }, []);

  /**
   * Fetch user stats
   */
  const fetchStats = useCallback(async () => {
    if (!targetUserId) return;

    try {
      // Fetch free play stats
      const { data: freeData } = await supabase
        .from("user_game_stats")
        .select("stats")
        .eq("user_id", targetUserId)
        .eq("game_slug", "sudoku")
        .eq("mode", "free")
        .single();

      if (freeData?.stats) {
        setStats(freeData.stats as SudokuUserStats);
      }

      // Fetch weekend stats if active weekend
      if (activeWeekend) {
        const { data: weekendData } = await supabase
          .from("user_game_stats")
          .select("stats")
          .eq("user_id", targetUserId)
          .eq("game_slug", "sudoku")
          .eq("mode", "weekend")
          .single();

        if (weekendData?.stats) {
          setWeekendStats(weekendData.stats as SudokuUserStats);
        }
      }
    } catch {
      // No stats yet
    }
  }, [targetUserId, activeWeekend]);

  /**
   * Initial data fetch
   */
  useEffect(() => {
    const loadData = async () => {
      setIsFetchingData(true);
      await Promise.all([
        fetchWeekendGameConfig(),
        fetchPuzzleCounts(),
        fetchStats(),
      ]);
      setIsFetchingData(false);
    };

    loadData();
  }, [fetchWeekendGameConfig, fetchPuzzleCounts, fetchStats]);

  /**
   * Start game with selected difficulty
   */
  const handleStartGame = async (difficulty: SudokuDifficulty) => {
    if (!targetUserId) return;

    setIsLoading(true);

    try {
      // Fetch a random puzzle
      const { count } = await supabase
        .from("sudoku_puzzles")
        .select("*", { count: "exact", head: true })
        .eq("difficulty", difficulty)
        .eq("is_active", true);

      if (!count || count === 0) {
        throw new Error("Geen puzzels beschikbaar");
      }

      const randomOffset = Math.floor(Math.random() * count);

      const { data: puzzle, error: puzzleError } = await supabase
        .from("sudoku_puzzles")
        .select("*")
        .eq("difficulty", difficulty)
        .eq("is_active", true)
        .range(randomOffset, randomOffset)
        .single();

      if (puzzleError || !puzzle) {
        throw new Error("Kon puzzel niet ophalen");
      }

      // Create initial game state
      const initialGameState = {
        puzzle_id: puzzle.id,
        difficulty: puzzle.difficulty,
        puzzle: puzzle.puzzle,
        solution: puzzle.solution,
        current: puzzle.puzzle,
        notes: {},
        moves: [],
        mistakes: 0,
        hints_used: 0,
        selected_cell: null,
        is_notes_mode: false,
        started_at: new Date().toISOString(),
        completed_at: null,
        is_completed: false,
        is_failed: false,
        time_elapsed_seconds: 0,
      };

      // Create game session
      const { data: session, error: sessionError } = await supabase
        .from("game_sessions")
        .insert({
          game_slug: "sudoku",
          created_by: targetUserId,
          participants: [
            {
              user_id: targetUserId,
              role: "player",
              name: activeProfile?.full_name ?? "Speler",
              team_id: userTeam?.id,
            },
          ],
          mode: selectedMode,
          session_type: "solo",
          status: "active",
          game_state: initialGameState,
          weekend_id: selectedMode === "weekend" ? activeWeekend?.id : null,
        })
        .select()
        .single();

      if (sessionError || !session) {
        throw new Error("Kon sessie niet aanmaken");
      }

      router.push(`/(tabs)/games/sudoku/${session.id}` as any);
    } catch (err) {
      console.error("[Sudoku] Error starting game:", err);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get stats for current mode and difficulty
   */
  const getStatsForDifficulty = (difficulty: SudokuDifficulty) => {
    const currentStats = selectedMode === "weekend" ? weekendStats : stats;
    return currentStats[difficulty];
  };

  /**
   * Get total stats for display
   */
  const getTotalStats = () => {
    const currentStats = selectedMode === "weekend" ? weekendStats : stats;
    const totals = {
      gamesPlayed: 0,
      gamesWon: 0,
      perfectGames: 0,
    };

    (["easy", "medium", "hard"] as SudokuDifficulty[]).forEach((diff) => {
      totals.gamesPlayed += currentStats[diff].games_played;
      totals.gamesWon += currentStats[diff].games_won;
      totals.perfectGames += currentStats[diff].perfect_games;
    });

    return totals;
  };

  const totalStats = getTotalStats();

  if (isFetchingData) {
    return (
      <SafeAreaView
        style={[styles.container, { backgroundColor: colors.background }]}
        edges={["top", "left", "right"]}
      >
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={[styles.loadingText, { color: colors.textSecondary }]}>
            Laden...
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: colors.background }]}
      edges={["top", "left", "right"]}
    >
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => router.back()}
          style={styles.backButton}
          accessibilityLabel="Terug"
          accessibilityRole="button"
        >
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>
        <View style={styles.headerContent}>
          <Text style={[styles.title, { color: colors.text }]}>
            🔢 Sudoku
          </Text>
          <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
            Vul het 9x9 raster met cijfers 1-9
          </Text>
        </View>
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.content}
        showsVerticalScrollIndicator={false}
      >
        {/* Game Mode */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>
            Spelmodus
          </Text>
          <GameModeSelector
            mode={selectedMode}
            onModeChange={setSelectedMode}
            weekendAvailable={isWeekendModeAvailable}
            weekendUnavailableReason={weekendUnavailableReason}
            isDark={isDark}
            compact
          />
        </View>

        {/* Stats Summary */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>
            {selectedMode === "weekend"
              ? "Weekend Statistieken"
              : "Jouw Statistieken"}
          </Text>
          <View
            style={[styles.statsCard, { backgroundColor: colors.surface }]}
          >
            <View style={styles.statItem}>
              <Text style={[styles.statValue, { color: colors.text }]}>
                {totalStats.gamesPlayed}
              </Text>
              <Text
                style={[styles.statLabel, { color: colors.textSecondary }]}
              >
                Gespeeld
              </Text>
            </View>
            <View style={styles.statDivider} />
            <View style={styles.statItem}>
              <Text style={[styles.statValue, { color: "#22C55E" }]}>
                {totalStats.gamesWon}
              </Text>
              <Text
                style={[styles.statLabel, { color: colors.textSecondary }]}
              >
                Gewonnen
              </Text>
            </View>
            <View style={styles.statDivider} />
            <View style={styles.statItem}>
              <Text style={[styles.statValue, { color: "#F59E0B" }]}>
                {totalStats.perfectGames}
              </Text>
              <Text
                style={[styles.statLabel, { color: colors.textSecondary }]}
              >
                Perfect 🏆
              </Text>
            </View>
          </View>
        </View>

        {/* Difficulty Selection */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>
            Kies moeilijkheid
          </Text>

          {(["easy", "medium", "hard"] as SudokuDifficulty[]).map(
            (difficulty) => {
              const config = DIFFICULTY_CONFIGS[difficulty];
              const diffStats = getStatsForDifficulty(difficulty);
              const points =
                selectedMode === "weekend"
                  ? weekendConfig.points_per_difficulty[difficulty]
                  : undefined;

              return (
                <DifficultyCard
                  key={difficulty}
                  config={config}
                  puzzleCount={puzzleCounts[difficulty]}
                  bestTime={diffStats.best_time_seconds}
                  gamesPlayed={diffStats.games_played}
                  gamesWon={diffStats.games_won}
                  points={points}
                  isWeekendMode={selectedMode === "weekend"}
                  onPress={handleStartGame}
                  disabled={isLoading || puzzleCounts[difficulty] === 0}
                />
              );
            }
          )}
        </View>

        {/* Leaderboard (Free Play only) */}
        {selectedMode === "free" && (
          <View style={styles.section}>
            <SudokuLeaderboard mode="free" maxEntries={5} />
          </View>
        )}

        {/* Weekend Mode Info */}
        {selectedMode === "weekend" && isWeekendModeAvailable && (
          <View style={styles.section}>
            <View
              style={[styles.infoCard, { backgroundColor: colors.surface }]}
            >
              <View style={styles.infoHeader}>
                <Ionicons name="trophy" size={20} color="#F59E0B" />
                <Text style={[styles.infoTitle, { color: colors.text }]}>
                  Weekend Modus
                </Text>
              </View>
              <Text
                style={[styles.infoText, { color: colors.textSecondary }]}
              >
                Verdien punten voor team {userTeam?.name}! Complete de puzzel
                zonder fouten en hints voor bonuspunten.
              </Text>
              {weekendConfig.perfect_bonus > 0 && (
                <View style={styles.bonusRow}>
                  <Ionicons name="star" size={14} color="#10B981" />
                  <Text style={[styles.bonusText, { color: "#10B981" }]}>
                    +{weekendConfig.perfect_bonus} punten voor perfecte score
                  </Text>
                </View>
              )}
            </View>
          </View>
        )}
      </ScrollView>

      {/* Loading Overlay */}
      {isLoading && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color="#FFFFFF" />
          <Text style={styles.loadingOverlayText}>Spel starten...</Text>
        </View>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    gap: 12,
  },
  loadingText: {
    fontSize: 16,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255, 255, 255, 0.1)",
  },
  backButton: {
    padding: 8,
    marginRight: 8,
  },
  headerContent: {
    flex: 1,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
  },
  subtitle: {
    fontSize: 14,
    marginTop: 2,
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 16,
    gap: 20,
    paddingBottom: 40,
  },
  section: {
    gap: 12,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "600",
  },
  statsCard: {
    flexDirection: "row",
    borderRadius: 16,
    padding: 16,
    justifyContent: "space-around",
  },
  statItem: {
    alignItems: "center",
    flex: 1,
  },
  statValue: {
    fontSize: 22,
    fontWeight: "bold",
  },
  statLabel: {
    fontSize: 11,
    marginTop: 4,
  },
  statDivider: {
    width: 1,
    backgroundColor: "rgba(255, 255, 255, 0.1)",
  },
  infoCard: {
    padding: 16,
    borderRadius: 16,
    gap: 8,
  },
  infoHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  infoTitle: {
    fontSize: 16,
    fontWeight: "600",
  },
  infoText: {
    fontSize: 14,
    lineHeight: 20,
  },
  bonusRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginTop: 4,
  },
  bonusText: {
    fontSize: 13,
    fontWeight: "500",
  },
  loadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    justifyContent: "center",
    alignItems: "center",
    gap: 12,
  },
  loadingOverlayText: {
    fontSize: 16,
    color: "#FFFFFF",
    fontWeight: "500",
  },
});

